<template>
  <SectionView class="nutrition-vitality" :title="title">
    <div class="health-waterfall-chart">
      <div class="chart-header" v-if="showHeader">
        <div class="warning-section">
          <div class="warning-title">预警提示</div>
          <div class="warning-count">
            <span class="count-number">{{ warningCount }}</span>
            <span class="count-total">/{{ totalItems }}</span>
          </div>
        </div>
        <div class="qualified-section">
          <div class="qualified-title">合格项目</div>
          <div class="qualified-count">
            <span class="count-number">{{ qualifiedCount }}</span>
            <span class="count-total">/{{ totalItems }}</span>
          </div>
        </div>
      </div>
      <div class="legend-container" v-if="showLegend">
        <div class="legend-item">
          <span class="legend-dot actual"></span>
          <span class="legend-text">异常数值</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot disk"></span>
          <span class="legend-text">正常数值</span>
        </div>
      </div>
      <div ref="chart" class="chart-container"></div>
    </div>
  </SectionView>
</template>

<script>
/**
 * 健康瀑布图组件
 * 用于展示健康指标数据，支持正负值展示，不同颜色渐变
 */
import SectionView from "@/components/SectionView";
export default {
  name: "HealthWaterfallChart",
  components: { SectionView },

  props: {
    title: {
      type: String,
      default: "营养活力提示",
    },
    // 图表数据
    data: {
      type: Array,
      required: true,
    },
    // 标签字段名
    labelKey: {
      type: String,
      default: "name",
    },
    // 值字段名
    valueKey: {
      type: String,
      default: "value",
    },
    // 是否显示头部统计
    showHeader: {
      type: Boolean,
      default: true,
    },
    // 预警数量
    warningCount: {
      type: [Number, String],
      default: 0,
    },
    // 合格数量
    qualifiedCount: {
      type: [Number, String],
      default: 0,
    },
    // 总项目数
    totalItems: {
      type: [Number, String],
      default: 16,
    },
    // 是否显示图例
    showLegend: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      chart: null,
    };
  },

  mounted() {
    this.initChart();

    // 监听窗口大小变化，重新渲染图表
    window.addEventListener("resize", this.resizeChart);

    // 添加自定义事件监听器
    window.addEventListener("chart-item-click", this.handleItemClick);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.resizeChart);
    // 移除自定义事件监听器
    window.removeEventListener("chart-item-click", this.handleItemClick);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },

  watch: {
    data: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
  },

  methods: {
    initChart() {
      this.chart = this.$echarts.init(this.$refs.chart);
      this.updateChart();
    },

    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    },

    updateChart() {
      if (!this.chart || !this.data || this.data.length === 0) return;

      const labels = this.data.map((item) => item[this.labelKey]);
      const values = this.data.map((item) => item[this.valueKey]);

      // 准备柱状图数据，包含渐变色和圆角设置
      const seriesData = values.map((value) => {
        if (value < 0) {
          return {
            value,
            itemStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "#FFF3F3" },
                  { offset: 1, color: "#FF5C4D" },
                ],
              },
              borderRadius: [0, 0, 50, 50],
            },
          };
        } else {
          // 正值柱状图：从 #FCFDFF 渐变到 #003CBF
          return {
            value,
            itemStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "#003CBF" },
                  { offset: 1, color: "#FCFDFF" },
                ],
              },
              borderRadius: [50, 50, 0, 0],
            },
          };
        }
      });

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderColor: "#eee",
          borderWidth: 1,
          padding: 15,
          textStyle: {
            color: "#333",
          },
          formatter: (params) => {
            const item = params[0];
            return `<div style="font-size:12px;color:#2C324E;font-weight:500;">
                      ${item.name}
                    </div>
                    <div>
                      <span style="font-size:10px;color:#64749C; font-weight:400;">实测值</span>
                      <span style="font-size:10px;color:#64749C;font-weight:500;  margin-left:20px ">${item.value}</span>
                    </div>`;
          },
          extraCssText:
            "box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);  padding: 10px;width: 80px;height: 50px;background: rgba(255,255,255,0.86);border-radius: 6px 6px 6px 6px;",
        },
        grid: {
          top: 20,
          bottom: 20,
          left: 20,
          right: 10,
        },
        dataZoom: [
          {
            type: "inside",
            startValue: 0,
            endValue: Math.min(4, this.data.length - 1),
            zoomLock: true,
            moveOnMouseWheel: true,
            moveOnMouseMove: true,
            preventDefaultMouseMove: false,
            throttle: 0,
            rangeMode: ["value", "value"],
            orient: "horizontal",
          },
        ],
        xAxis: {
          type: "category",
          data: labels,
          axisLine: {
            lineStyle: {
              color: "#E8E8E8",
            },
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            interval: 0,
            fontSize: 12,
            color: "#959FB7",
            formatter: function (value) {
              // 如果文字太长，可以截断
              // if (value.length > 2) {
              //   return value.substring(0, 3) + "..";
              // }
              return value;
            },
          },
        },
        yAxis: {
          type: "value",
          min: -4.0,
          max: 4.0,
          interval: 1.0,
          axisLine: {
            show: false,
            lineStyle: {
              color: "#CCCCCC",
            },
          },
          axisTick: {
            show: false,
          },

          splitLine: {
            show: true,
            lineStyle: {
              type: "dashed",
              color: "#CCCCCC",
            },
          },
          axisLabel: {
            formatter: "{value}",
          },
        },
        series: [
          {
            type: "bar",
            barWidth: "30%",
            barMaxWidth: 30,
            data: seriesData,
          },
        ],
      };

      this.chart.setOption(option);
      // 默认选中第一个柱状图
      if (this.data.length > 0) {
        this.chart.dispatchAction({
          type: "highlight",
          seriesIndex: 0,
          dataIndex: 0,
        });

        // 显示第一个柱状图的提示框
        this.chart.dispatchAction({
          type: "showTip",
          seriesIndex: 0,
          dataIndex: 0,
        });
      }
      // 添加图表点击事件
       this.chart.off('click');
      this.chart.on('click', (params) => {
        console.log('图表点击事件触发:', params);
        if (params.dataIndex >= 0 && params.dataIndex < this.data.length) {
          const item = this.data[params.dataIndex];
          console.log('点击的数据项:', item);
          this.$emit('item-click', item);
        }
      });
      // 添加图表点击事件
      // this.chart.off('click');
      // this.chart.on('click', (params) => {
      //   console.log('图表点击事件触发:', params);
      //   if (params.dataIndex >= 0 && params.dataIndex < this.data.length) {
      //     const item = this.data[params.dataIndex];
      //     console.log('点击的数据项:', item);
      //     this.$emit('item-click', item);

      //     // 更新 x 轴标签样式
      //     this.updateAxisLabelStyle(params.dataIndex);
      //   }
      // });
    },
  },
};
</script>

<style lang="scss" scoped>
.health-waterfall-chart {
  width: 100%;
  background-color: #f5f5f5;

  .chart-header {
    display: flex;
    border-bottom: 1px solid #f0f0f0;

    .warning-section,
    .qualified-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;

      .warning-title,
      .qualified-title {
        font-size: 32px;
        margin-bottom: 5px;
      }

      .warning-count,
      .qualified-count {
        .count-number {
          font-size: 32px;
          font-weight: bold;
        }

        .count-total {
          font-size: 16px;
          color: #999;
        }
      }
    }

    .warning-section {
      .warning-title {
        color: #ff5c4d;
      }

      .count-number {
        color: #ff5c4d;
      }
    }

    .qualified-section {
      .qualified-title {
        color: #003cbf;
      }

      .count-number {
        color: #003cbf;
      }
    }
  }

  .chart-container {
    height: 500px;
    width: 100%;
  }

  .legend-container {
    display: flex;
    justify-content: flex-end;
    margin: 10px 0;

    .legend-item {
      display: flex;
      align-items: center;
      margin: 0 15px;

      .legend-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 5px;

        &.actual {
          background-color: #ff5c4d;
        }

        &.standard {
          background-color: #c3c2c2;
        }

        &.disk {
          background-color: #003cbf;
        }
      }

      .legend-text {
        color: #00133d;
        font-weight: 400;
        font-size: 20px;
        color: #666;
      }
    }
  }
}
</style>
